import React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';

import { PlainButton } from 'ui/public/button';
import { TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { ClockInput } from 'ui/standard/input/clock_input';
import { WeekdayInput } from 'ui/standard/input/weekday_input';

import { PolicyInput } from 'ui/schedules/policy_input/policy_input';
import { DeviceInput } from 'ui/schedules/device_input/device_input';

import { ResultKind } from 'services/base/types';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain, Schedule } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { EditScheduleStore } from './edit_schedule_store';
import Messages from './edit_schedule_messages';
import baseStyles from './edit_schedule.module.css';

type EditScheduleStyles = {
  headerContainer: string;
  saveButton: string;
  ruleContainer: string;
  labelContainer: string;
  inputLabel: string;
  ruleTitle: string;
  manageDevicesContainer: string;
};

const themedStyles: ThemedStyles<EditScheduleStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: baseStyles.headerContainer,
    saveButton: baseStyles.daylightSaveButton,
    ruleContainer: baseStyles.daylightRuleContainer,
    labelContainer: baseStyles.labelContainer,
    inputLabel: baseStyles.daylightInputLabel,
    ruleTitle: baseStyles.daylightRuleTitle,
    manageDevicesContainer: baseStyles.manageDevicesContainer,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: baseStyles.headerContainer,
    saveButton: baseStyles.midnightSaveButton,
    ruleContainer: baseStyles.midnightRuleContainer,
    labelContainer: baseStyles.labelContainer,
    inputLabel: baseStyles.midnightInputLabel,
    ruleTitle: baseStyles.midnightRuleTitle,
    manageDevicesContainer: baseStyles.manageDevicesContainer,
  },
};

type DetailsSectionProps = {
  store: EditScheduleStore;
};

const DetailsSection = observer(({ store }: DetailsSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <TextInput store={store.name} placeholder={Messages.namePlaceholder()}/>
      <ErrorMessage message={store.nameError}/>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.startTimeLabel()}</span>
        <ClockInput store={store.start}/>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.endTimeLabel()}</span>
        <ClockInput store={store.end}/>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.activeDaysLabel()}</span>
        <WeekdayInput store={store.activeDays} placeholder={Messages.activeDaysPlaceholder()}/>
      </div>
      <ErrorMessage message={store.daysError}/>
    </div>
  );
});

type PoliciesSectionProps = {
  store: EditScheduleStore;
};

const PoliciesSection = ({ store }: PoliciesSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.blockedAppsLabel()}</div>
      <PolicyInput store={store.policies} placeholder={Messages.blockedAppsPlaceholder()}/>
    </div>
  );
};

type DevicesSectionProps = {
  domain: IPangolinDomain;
  store: EditScheduleStore;
};

const DevicesSection = ({ domain, store }: DevicesSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.manageDevicesContainer}>
      <div className={styles.ruleTitle}>{Messages.managedDevicesLabel()}</div>
      <DeviceInput
        placeholder={Messages.addDevicePlaceholder()}
        hosts={domain.hosts}
        store={store.devices}
      />
    </div>
  );
};

type BaseEditScheduleProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  schedule: Schedule;
};

export const BaseEditSchedule = observer(({ pangolin, domain, schedule }: BaseEditScheduleProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const hosts = domain.hosts;

  const [store, setStore] = React.useState(() => new EditScheduleStore(schedule, hosts));
  React.useEffect(() => { setStore(new EditScheduleStore(schedule, hosts)); }, [schedule, hosts]);

  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, domain, history]);
  return (
    <StandardPage>
      <form onSubmit={handleSave}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            disabled={!pangolin.onlineStatus}
            className={styles.saveButton}>{Messages.save()}</PlainButton>
        </div>
        <DetailsSection store={store}/>
        <PoliciesSection store={store}/>
        <DevicesSection domain={domain} store={store}/>
      </form>
    </StandardPage>
  );
});

export const EditSchedule = observer(() => {
  const { pangolinID, scheduleID } = useParams<{ pangolinID: string, scheduleID: string }>();
  if (!pangolinID) return <Redirect />;
  if (!scheduleID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  const schedule = domain.schedules.get(scheduleID);
  if (!schedule) return <Redirect />;

  return (
    <BaseEditSchedule
      pangolin={pangolin}
      domain={domain}
      schedule={schedule}
    />
  );
});
